/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Dropdown Menu Component
 *
 * @file DropdownMenu.jsx
 * @author Murhsid Ahamedh
 */

import React from 'react';
import PropTypes from 'prop-types';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import MoreVertIcon from '@material-ui/icons/MoreVert';

const baseStyles = {
  container: {
    position: 'relative',
    display: 'inline-block'
  },
  trigger: {
    padding: 0
  },
  menuPaper: {
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    marginTop: 0
  },
  menuItem: {
    fontSize: '0.875em'
  }
};

const DropdownMenu = ({
  items,
  triggerAriaLabel,
  dropdownStyle,
  menuItemStyle,
  triggerStyle,
  triggerHoverStyle,
  triggerContent,
  renderItem,
  usePortal,
  frameDoc
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [isHovered, setIsHovered] = React.useState(false);
  const [hoveredId, setHoveredId] = React.useState(null);

  const open = Boolean(anchorEl);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const container = frameDoc ? frameDoc.body : undefined;

  const Trigger = (
    <IconButton
      aria-label={triggerAriaLabel}
      aria-haspopup="true"
      aria-controls={open ? 'dropdown-menu' : undefined}
      onClick={handleOpen}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        ...baseStyles.trigger,
        ...(isHovered ? (triggerHoverStyle || {}) : {}),
        ...triggerStyle
      }}
    >
      {triggerContent || <MoreVertIcon />}
    </IconButton>
  );

  return (
    <div style={baseStyles.container}>
      {Trigger}
      <Menu
        id="dropdown-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ style: { ...baseStyles.menuPaper, padding: 0, ...dropdownStyle } }}
        MenuListProps={{ disablePadding: true, style: { padding: 0 } }}
        container={usePortal ? container : undefined}
        disablePortal={!usePortal}
      >
        {items.map((item, index) => (
          renderItem ? (
            <div
              key={item.id}
              onClick={handleClose}
              onMouseEnter={() => setHoveredId(item.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {renderItem(item, hoveredId === item.id, handleClose)}
            </div>
          ) : (
            <MenuItem
              key={item.id}
              onClick={(e) => {
                e.stopPropagation();
                if (typeof item.onClick === 'function') item.onClick();
                handleClose();
              }}
              style={{ ...baseStyles.menuItem, ...menuItemStyle, ...(index === items.length - 1 ? { borderBottom: 'none' } : {}) }}
            >
              {item.label}
            </MenuItem>
          )
        ))}
      </Menu>
    </div>
  );
};

DropdownMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, label: PropTypes.string.isRequired, onClick: PropTypes.func, href: PropTypes.string })
  ).isRequired,
  triggerAriaLabel: PropTypes.string,
  dropdownStyle: PropTypes.object,
  menuItemStyle: PropTypes.object,
  triggerStyle: PropTypes.object,
  triggerHoverStyle: PropTypes.object,
  triggerContent: PropTypes.node,
  renderItem: PropTypes.func,
  usePortal: PropTypes.bool,
  frameDoc: PropTypes.object
};

DropdownMenu.defaultProps = {
  triggerAriaLabel: 'Open menu',
  dropdownStyle: {},
  menuItemStyle: {},
  triggerStyle: {},
  triggerHoverStyle: {},
  triggerContent: null,
  renderItem: null,
  usePortal: true,
  frameDoc: null
};

export default DropdownMenu;

